/**
 * The bounded feed fetch, shared by the list (`/feed`) and one job from it (`/feed/:id`).
 *
 * Moved out of `routes/feed.tsx` by `W4-T10`, because the provider's job view has no endpoint of its
 * own — `GET /api/jobs/:id` is owner-only — and reads the same set through the same query key. Warm
 * when the provider came from the list; one bounded fetch when they followed a link.
 */
import { type JobFeedItem } from '@marketplace/contracts';
import { type RouterContextProvider } from 'react-router';
import { queryKeys, routeContext } from '../../shared/query.js';

/** The bound. Five pages of the contract's default, and a feed reaching it is rendered as truncated. */
export const MAX_FEED_JOBS = 100;

export async function loadJobFeed(
  context: Readonly<RouterContextProvider>,
): Promise<JobFeedItem[]> {
  const { queryClient, api } = context.get(routeContext);

  return queryClient.ensureQueryData({
    queryKey: queryKeys.jobFeed(),
    queryFn: async () => {
      const jobs: JobFeedItem[] = [];
      let cursor: string | undefined;

      // Bounded by `MAX_FEED_JOBS` *and* by the cursor going null, so an API that kept handing back
      // the same cursor would end this loop rather than spin it.
      while (jobs.length < MAX_FEED_JOBS) {
        const page = await api.getJobFeed(cursor);
        jobs.push(...page.items);
        if (!page.page.hasMore || page.page.nextCursor === null) break;
        cursor = page.page.nextCursor;
      }
      return jobs;
    },
  });
}
